// Tipping math + validation — pure logic extracted from Tipping.tsx so the
// over-tip soft-cap and the bin/weight arithmetic are unit-testable
// (PACK-013…017). The DB enforces the hard invariant (bins_remaining can't go
// negative — see bins_conservation_balance.sql pgTAP); this is the UI layer that
// stops an operator attempting an over-tip in the first place.

export interface TipFormState {
  /** Parsed gross weight in kg (parseFloat(grossKg) || 0). */
  gross: number
  /** Selected bin quantity (always ≥ 1 by construction in the form). */
  binQty: number
  /** Live bins_remaining on the delivery (refetched after every tip). */
  binsRemaining: number
}

export type TipValidation = { ok: true } | { ok: false; message: string }

// Mutation-time guard for the tip form fields. Order matters and mirrors
// Tipping.tsx: gross → bin floor → over-tip; the first failing check wins so the
// operator sees the most fundamental problem first. Authentication is checked
// separately in the component (it's a session concern, not tipping math).
export function validateTip(state: TipFormState): TipValidation {
  if (state.gross <= 0)       return { ok: false, message: 'Enter a valid gross weight' }
  if (state.binQty < 1)       return { ok: false, message: 'Bin quantity must be at least 1' }
  if (state.binQty > state.binsRemaining)
    return { ok: false, message: `Only ${state.binsRemaining} bins remaining on this delivery` }
  return { ok: true }
}

// Save-button enabled predicate — the UI soft cap, independent of auth (checked
// at submit) and of the pending flag (React state). Disabled unless there's a
// positive gross, at least one bin remaining, and the selection fits.
//
// netKg is the net the server will compute (tipNetWeight on the delivery's
// resolved tare). The server refuses a net of zero or below (23514), so the UI
// gate matches it: undefined or null = not checked (no usable tare, e.g. offline:
// the tip still goes through the queue and the server checks), a number must be
// above zero.
export function canSubmitTip(state: TipFormState, netKg?: number | null): boolean {
  if (netKg != null && !(netKg > 0)) return false
  return state.gross > 0 && state.binsRemaining >= 1 && state.binQty <= state.binsRemaining
}

// Net weight = gross minus the tare of the bins tipped. null until a positive
// gross is entered (so the form shows nothing rather than a misleading negative).
export function tipNetWeight(gross: number, binQty: number, binTareKg: number): number | null {
  return gross > 0 ? gross - binQty * binTareKg : null
}

// Whole-percent of a delivery's bins tipped so far. 0 when nothing was received
// (avoids divide-by-zero). Shared by the form header and the ready-to-tip list.
export function tippedPercent(binsReceived: number, binsRemaining: number): number {
  return binsReceived > 0
    ? Math.round(((binsReceived - binsRemaining) / binsReceived) * 100)
    : 0
}

// Clamp a candidate bin quantity into [1, max(1, binsRemaining)] — used by the
// "+" stepper and by the clamp-down effect when another device tips the
// remaining count below the current selection. The lower bound stays at 1 even
// when nothing remains (the Save button, via canSubmitTip, blocks the submit).
export function clampBinQty(qty: number, binsRemaining: number): number {
  return Math.min(Math.max(1, qty), Math.max(1, binsRemaining))
}
